import 'c/main/lib/components/lazyloader.scss'

import extend from 'extend'
import modernizr from 'modernizr'

import { htmlToEls, addElements, removeElements } from 'j/main/lib/tools/dom'
import { addEventListener } from 'j/main/lib/tools/events'
import { standardHandlerClass } from 'j/main/lib/tools/init'
import { getOffset } from 'j/main/lib/tools/scrolling'
import { makeArray, forEach, findFilter, filter } from 'j/main/lib/tools/traversing'
import { AjaxLoader, changeUrlParameter } from 'j/main/lib/utilities/ajax'

/**
  @class LazyLoader

  Lazily loads paginated items.
*/
const LazyLoader = standardHandlerClass(class extends AjaxLoader {
  static baseConf = {
    navSelector: '.pagination-nav',
    autoNextSelector: '.pagination-lazyload-next',
    manualNextSelector: '.pagination-manual-next',
    threshold: 300
  }

  /**
   * Constructs an instance of LazyLoader.
   *
   * @param {HtmlElement} el The element container the lazy loaded content
   * @param {Object} [conf] Lazyloader options
   * @param {string} [conf.containerSelector] Used to find this.el in incoming html. Defaults to this.el.classList.
   * @param {string} [conf.navSelector] Defaults to '.pagination-nav'
   * @param {string} [conf.autoNextSelector] Defaults to '.pagination-lazyload-next'
   * @param {string} [conf.manualNextSelector] Defaults to '.pagination-manual-next'
   * @param {int} [conf.threshold] The threshold (in vh) at which to start automatically loading content. Defaults to 300.
   *
   * @constructs LazyLoader
   */
  constructor (el, conf = {}) {
    super()

    this.el = el
    this.conf = extend(LazyLoader.baseConf, conf)

    if (!this.conf.containerSelector) {
      this.conf.containerSelector = makeArray(this.el.classList).join('.')
    }

    this.nav = this.el.querySelector(this.conf.navSelector)

    if (!this.nav) {
      return
    }

    this.autoNext = this.nav.querySelector(this.conf.autoNextSelector)
    this.allNexts = this.nav.querySelectorAll(
      this.conf.autoNextSelector + ', ' + this.conf.manualNextSelector)

    // ajax on click of pagination link
    forEach(this.allNexts, next => {
      addEventListener(next, 'click', (e) => {
        e.preventDefault()
        this.pageRequestHandler(next.getAttribute('href'))
      })
    })

    if (this.autoNext) {
      let adjust
      this.resizeHandler = (winWidth, winHeight) => {
        adjust = winHeight * this.conf.threshold / 100
      }
      this.scrollHandler = (scrollTop, scrollLeft) => {
        // if currently loading either a new page or some previous lazy
        // loaded content, do nothing
        if (this.getCurrentPromise()) {
          return
        }

        if (scrollTop > getOffset(this.nav).top - adjust) {
          this.pageRequestHandler(this.autoNext.getAttribute('href'))
        }
      }
    }
  }

  /**
   * Requests the page specified by this.url, then calls this.insert.
   *
   * @method LazyLoader#pageRequestHandler
   */
  pageRequestHandler (url) {
    const promise = this.requestPage(changeUrlParameter(url, 'ajax', 1))
    // promise should never be null here because this should never request a
    // page we're already fetching, but on the off chance we are, do nothing.
    if (promise) {
      promise.then((requestPageRv) => {
        this.insert(requestPageRv[1])
      }).catch((err) => {
        console.log(err)
        // if there is an error, redirect user to url so they see the
        // appropriate error page
        if (!err.cancelled) {
          window.location = changeUrlParameter(url, 'ajax')
        }
      })
    }
  }

  /**
   * Inserts the content contained in this.newEls into the DOM.
   *
   * @method LazyLoader#insert
   */
  insert (newContent) {
    const newContainer = newContent.querySelector(this.conf.containerSelector)
    // Convert this to an array as .children returns an HtmlCollection
    // which will automatically update if the children of #content change.
    let newEls = makeArray(newContainer.children)
    const newNav = filter(newEls, this.conf.navSelector)[0]

    forEach(newEls, newEl => {
      newEl.classList.add('pageloader-incoming')
    })
    newNav.classList.remove('pageloader-incoming')

    removeElements(this.nav)
    this.nav = newNav
    this.autoNext = this.nav.querySelector(this.conf.autoNextSelector)
    this.allNexts = this.nav.querySelectorAll(this.conf.autoNextSelector +
                                              ', ' +
                                              this.conf.manualNextSelector)

    if (!this.autoNext) {
      this.teardown()
    }

    addElements(newEls, this.el)
    this.postInsert(newEls)
  }

  /**
   * Transitions in the elements added to the DOM by insert.
   *
   * @method LazyLoader#postInsert
   */
  postInsert (newEls) {
    forEach(newEls, newEl => {
      newEl.classList.remove('pageloader-incoming')
    })
  }

  /**
   * We maintain a cache of all pages loaded by modules that inherit from
   * AjaxLoader, so that the back button can return the user to the correct
   * position of lazily loaded content. This updates that cache.
   *
   * @method LazyLoader#updateCache
   * @param {string} url cached page url, used a cache key
   * @param {string} title title of the cached page
   * @param {string} html html to be added to the cached page
   */
  updateCache (url, title, newHtml) {
    // remove page parameter, since we're updating the existing cache
    const cacheKey = this.getCacheKey(changeUrlParameter(url, 'page'))

    // Get the cached content. The initial page is cached - see ajax.js.
    const cached = this.getCache()[cacheKey]
    // May not be a cache if we're not using pageloader
    if (cached) {
      const pageTitle = cached[0]
      const cachedContent = htmlToEls(cached[1])[0]
      const cachedContainer =
        findFilter(cachedContent, this.conf.containerSelector)[0]
      const cachedNav = cachedContainer.querySelector(this.conf.navSelector)
      cachedNav.parentNode.removeChild(cachedNav)

      const newContainer = findFilter(
        htmlToEls(newHtml), this.conf.containerSelector)[0]
      forEach(newContainer.children, newEl => {
        cachedContainer.appendChild(newEl.cloneNode(true))
      })
      this.getCache()[cacheKey] = [pageTitle, cachedContent.outerHTML]
    }
  }
})

export default LazyLoader
